import { createSelector } from 'reselect'
import { sum, uniqueBy } from 'lib/array/arrayUtils'
import { checkoutAccommodationOfferView } from 'checkout/selectors/view/accommodation'
import { getVillaViews } from 'checkout/selectors/view/villa'
import { getFlightTotals } from 'checkout/selectors/payment/flights'
import luxPlusInclusionFilter from 'lib/offer/luxPlusInclusionFilter'
import { isSpoofed } from 'selectors/featuresSelectors'
import { getIsLuxPlusLPPEnabled } from '../featureToggle'

export const getAvailableAccommodationBenefits = createSelector(
  (state: App.State) => checkoutAccommodationOfferView(state),
  (state: App.State) => getVillaViews(state),
  (state: App.State) => state.luxPlus.products.subscription,
  (state: App.State) => getFlightTotals(state),
  (state: App.State) => isSpoofed(state),
  (state: App.State) => getIsLuxPlusLPPEnabled(state),
  (accommodationOfferView, villaViews, productSubscription, flightTotal, isSpoofed, luxPlusLPPEnabled): App.Checkout.LuxPlusAccommodationBenefits => {
    let totalPrice = 0
    let totalBaseMemberPrice = 0
    let totalMemberPrice = 0
    let totalMemberPriceSavings = 0

    const hotelViews = accommodationOfferView.data.flatMap<App.Checkout.LEAccommodationItemView>(view => {
      return view.itemViews.filter(itemView => itemView.kind === 'le')
    })

    const bedbankViews = accommodationOfferView.data.flatMap<App.Checkout.BedbankAccommodationItemView>(view => {
      return view.itemViews.filter(itemView => itemView.kind === 'bedbank')
    })

    const toursV2Views = accommodationOfferView.data.flatMap<App.Checkout.TourV2AccommodationItemView>(view => {
      return view.itemViews.filter(itemView => itemView.kind === 'tourV2')
    })

    // Use the separate villa views since they're not in the accommodation offer view yet
    const villaItemViews = villaViews.data

    const isBundle = accommodationOfferView.data.some(view => view.isBundled)
    const flightsPrice = flightTotal.data.price
    const isTourV2 = toursV2Views.length > 0
    const hasVillas = villaItemViews.length > 0

    if (hotelViews.length > 0) {
      totalPrice += sum(hotelViews, view => view.totals.price + (view.totals.surcharge ?? 0) + (view.totals.otherFees?.propertyFees ?? 0))
      totalBaseMemberPrice += sum(hotelViews, view => view.totals.memberPrice)
      totalMemberPrice += sum(hotelViews, view => (view.totals.memberPrice || view.totals.price) + (view.totals.surcharge ?? 0) + (view.totals.otherFees?.propertyFees ?? 0))
      totalMemberPriceSavings += sum(hotelViews, view => view.totals.memberPrice > 0 ? view.totals.price - view.totals.memberPrice : 0)
    }

    if (bedbankViews.length > 0 && luxPlusLPPEnabled) {
      totalPrice += sum(bedbankViews, view => view.totals.price + (view.totals.surcharge ?? 0) + (view.totals.otherFees?.propertyFees ?? 0))
      totalBaseMemberPrice += sum(bedbankViews, view => view.totals.memberPrice)
      totalMemberPrice += sum(bedbankViews, view => (view.totals.memberPrice || view.totals.price) + (view.totals.surcharge ?? 0) + (view.totals.otherFees?.propertyFees ?? 0))
      totalMemberPriceSavings += sum(bedbankViews, view => view.totals.memberPrice > 0 ? view.totals.price - view.totals.memberPrice : 0)
    }

    if (isTourV2) {
      totalPrice += sum(toursV2Views, view => view.totals.price)
      totalBaseMemberPrice += sum(toursV2Views, view => view.totals.memberPrice)
      totalMemberPrice += sum(toursV2Views, view => Number(view.totals.memberPrice))
      totalMemberPriceSavings += sum(toursV2Views, view => Number(view.totals.memberPrice) > 0 ? view.totals.price - view.totals.memberPrice : 0)
    }

    if (hasVillas) {
      totalPrice += sum(villaItemViews, view => view.totals.price + (view.totals.surcharge ?? 0))
      totalBaseMemberPrice += sum(villaItemViews, view => view.totals.memberPrice)
      totalMemberPrice += sum(villaItemViews, view => (view.totals.memberPrice || view.totals.price) + (view.totals.surcharge ?? 0))
      totalMemberPriceSavings += sum(villaItemViews, view => view.totals.memberPrice > 0 ? view.totals.price - view.totals.memberPrice : 0)
    }

    if (isBundle) {
      totalPrice = totalPrice + flightsPrice
      totalMemberPrice = totalBaseMemberPrice > 0 ? totalMemberPrice + flightsPrice : 0
    }

    const hasEarlyAccess = accommodationOfferView.data.some(view => view.offer?.luxPlus?.access === 'earlyAccess')
    const isMemberOnly = accommodationOfferView.data.some(view => view.offer?.luxPlus?.access === 'memberOnly')
    const membershipRequired = hasEarlyAccess || isMemberOnly
    const offersWithJoinFeeWaived = productSubscription?.offers?.filter(offer => offer.joinFeeWaived)

    let hasLuxPlusInclusions = false
    const subscriptionViews = offersWithJoinFeeWaived?.map<App.Checkout.LuxPlusCheckoutBenefitsSubscriptionViewWithInclusions>(offer => {
      // Combine inclusions from hotels and villas
      const hotelInclusions = hotelViews.flatMap(view => {
        const luxPlusInclusions = view.pkg?.luxPlusInclusionsByTier?.[offer.tier] ?? []
        return luxPlusInclusionFilter(luxPlusInclusions, [...view.bonusInclusions, ...view.inclusions])
      })

      const villaInclusions = villaItemViews.flatMap(view => {
        const luxPlusInclusions = view.package?.luxPlusInclusionsByTier?.[offer.tier] ?? []
        return luxPlusInclusions
      })

      const inclusions = [...hotelInclusions, ...villaInclusions]

      if (inclusions.length) {
        hasLuxPlusInclusions = true
      }

      return {
        inclusions: uniqueBy(inclusions, incl => incl.description),
        offer,
      }
    }) ?? []

    return {
      totalPrice,
      totalBaseMemberPrice,
      totalMemberPrice,
      totalMemberPriceSavings,
      hasEarlyAccess,
      isMemberOnly,
      membershipRequired: membershipRequired && !isSpoofed,
      subscriptionViews,
      inclusions: subscriptionViews.flatMap(view => view.inclusions) ?? [],
      isBundledWithFlights: isBundle,
      isTourWithMemberPrice: isTourV2 && totalMemberPrice > 0,
      hasLuxPlusInclusions,
    }
  })
